"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  Text,
  FormErrorMessage,
  useToast,
} from "@chakra-ui/react";
import { TbCalendarSearch } from "react-icons/tb";

interface CalendarAvailabilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: any) => void;
  initialValues?: any;
}

export const CalendarAvailabilityModal: React.FC<
  CalendarAvailabilityModalProps
> = ({ isOpen, onClose, onSave, initialValues }) => {
  const [name, setName] = useState("check_calendar_availability");
  const [description, setDescription] = useState(
    "When users ask for availability, check the calendar and provide available slots."
  );
  const [apiKey, setApiKey] = useState("");
  const [eventTypeId, setEventTypeId] = useState("");
  const [timezone, setTimezone] = useState("");
  const [nameError, setNameError] = useState("");
  const toast = useToast();

  useEffect(() => {
    if (initialValues) {
      setName(initialValues.name || "check_calendar_availability");
      setDescription(
        initialValues.description ||
          "When users ask for availability, check the calendar and provide available slots."
      );
      setApiKey(initialValues.cal_api_key || "");
      setEventTypeId(
        initialValues.event_type_id?.toString() ||
          initialValues.cal_event_type_id ||
          ""
      );
      setTimezone(initialValues.timezone || "America/Los_Angeles");
    } else {
      setName("check_calendar_availability");
      setDescription(
        "When users ask for availability, check the calendar and provide available slots."
      );
      setApiKey("");
      setEventTypeId("");
      setTimezone("America/Los_Angeles");
    }
  }, [initialValues]);

  const validateToolName = (value: string) => {
    if (!value.trim()) {
      setNameError("Tool name cannot be empty");
      return false;
    }
    if (value.length > 64) {
      setNameError("Tool name must be 64 characters or less");
      return false;
    }
    const validNameRegex = /^[a-zA-Z0-9_-]+$/;
    if (!validNameRegex.test(value)) {
      setNameError(
        "Tool name must only contain letters, numbers, underscores, and dashes (no spaces)"
      );
      return false;
    }
    setNameError("");
    return true;
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setNameError("");
  };

  const handleSave = () => {
    if (!validateToolName(name)) {
      toast({
        title: "Invalid tool name",
        description: nameError,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    onSave({
      type: "check_availability_cal",
      name,
      description,
      cal_api_key: apiKey,
      event_type_id: Number(eventTypeId),
      timezone,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent bg="white" color="gray.800">
        <ModalHeader
          display="flex"
          alignItems="center"
          gap={2}
          color="secondary.800"
        >
          <TbCalendarSearch size={20} />
          <span>Check Calendar Availability (Cal.com)</span>
        </ModalHeader>
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired isInvalid={!!nameError}>
              <FormLabel color="secondary.700">Name</FormLabel>
              <Input
                id="check-calendar-availability-function-name"
                value={name}
                onChange={handleNameChange}
                onBlur={() => validateToolName(name)}
                placeholder="Enter tool name"
                variant="outline"
                bg="white"
                color="gray.800"
                borderColor="gray.300"
                _hover={{ borderColor: "gray.400" }}
                _placeholder={{ color: "gray.500" }}
                focusBorderColor="primary.500"
              />
              {nameError && <FormErrorMessage>{nameError}</FormErrorMessage>}
            </FormControl>

            <FormControl>
              <FormLabel color="secondary.700">
                Description (Optional)
              </FormLabel>
              <Textarea
                id="check-calendar-availability-function-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter function description"
                variant="outline"
                bg="white"
                color="gray.800"
                borderColor="gray.300"
                _hover={{ borderColor: "gray.400" }}
                _placeholder={{ color: "gray.500" }}
                focusBorderColor="primary.500"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel color="secondary.700">API Key (Cal.com)</FormLabel>
              <Input
                id="check-calendar-availability-function-api-key"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Enter Cal.com API key"
                type="password"
                variant="outline"
                bg="white"
                color="gray.800"
                borderColor="gray.300"
                _hover={{ borderColor: "gray.400" }}
                _placeholder={{ color: "gray.500" }}
                focusBorderColor="primary.500"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel color="secondary.700">
                Event Type ID (Cal.com)
              </FormLabel>
              <Input
                id="check-calendar-availability-function-event-type-id"
                value={eventTypeId}
                onChange={(e) => setEventTypeId(e.target.value)}
                placeholder="Enter Event Type ID"
                variant="outline"
                bg="white"
                color="gray.800"
                borderColor="gray.300"
                _hover={{ borderColor: "gray.400" }}
                _placeholder={{ color: "gray.500" }}
                focusBorderColor="primary.500"
              />
              <Text fontSize="sm" color="gray.600" mt={1}>
                Must be a numeric value (e.g., 2505265)
              </Text>
            </FormControl>

            <FormControl>
              <FormLabel color="secondary.700">Timezone (Optional)</FormLabel>
              <Input
                id="check-calendar-availability-function-timezone"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                placeholder="Enter Timezone (e.g., America/Los_Angeles)"
                variant="outline"
                bg="white"
                color="gray.800"
                borderColor="gray.300"
                _hover={{ borderColor: "gray.400" }}
                _placeholder={{ color: "gray.500" }}
                focusBorderColor="primary.500"
              />
              <Text fontSize="sm" color="gray.600" mt={1}>
                Default: America/Los_Angeles
              </Text>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            bg="primary.500"
            color="white"
            _hover={{ bg: "primary.600" }}
            onClick={handleSave}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
